import React from 'react';
import { ListGroup } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { normalizeGoalType, goalTypeToUrl } from '../utils/goalTypeUtils';
import { getStatusColor } from '../utils/euStatusUtils';

// Helper to get trend arrow
const getTrendArrow = (category) => {
  if (!category) return { arrow: '—', color: '#bdbdbd' };
  const cat = String(category).toLowerCase();
  if (cat.includes('on track') || cat.includes('target met')) return { arrow: '↑', color: '#16a34a' };
  if (cat.includes('moderate')) return { arrow: '↗', color: '#fcc30c' };
  if (cat.includes('marginal') || cat.includes('stagnation') || cat.includes('stagnating')) return { arrow: '→', color: '#f67c01' };
  if (cat.includes('regression') || cat.includes('decreasing')) return { arrow: '↓', color: '#d3302f' };
  return { arrow: '—', color: '#bdbdbd' };
};

const TargetsList = ({ targets, goalId, activeTargetId, onTargetSelect }) => {
  const { goalType: urlGoalType, targetId } = useParams(); // Get goalType and targetId from URL parameters
  const goalType = normalizeGoalType(urlGoalType);
  const isUN = goalType === 'un';

  const handleTargetClick = (targetId) => {
    onTargetSelect(targetId); // Update selected target ID in parent component
  };

  // Use targetId from URL if available, otherwise use activeTargetId prop
  // Convert to string for comparison
  const currentActiveId = targetId ? String(targetId) : (activeTargetId ? String(activeTargetId) : null);

  if (!targets || targets.length === 0) {
    return (
      <div className="targets-list-empty" style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, sans-serif' }}>
        No targets in this goal yet.
      </div>
    );
  }

  return (
    <div className="sidebar-entity-list">
      <ListGroup className="sidebar-entity-list__group" style={{ border: 'none' }}>
        {targets.map(target => (
          <Link
            key={target.id}
            to={`/${goalTypeToUrl(goalType)}/goal/${goalId}/targets/${target.id}`}
            className={`list-group-item list-group-item-action sidebar-entity-list__item ${String(target.id) === currentActiveId ? 'active' : ''}`}
            onClick={() => handleTargetClick(target.id)}
            style={{
              fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, sans-serif',
              fontSize: '0.875rem',
              lineHeight: '1.5',
              color: '#334155',
              backgroundColor: String(target.id) === currentActiveId ? '#e3f2fd' : '#fff',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (String(target.id) !== currentActiveId) {
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }
            }}
            onMouseLeave={(e) => {
              if (String(target.id) !== currentActiveId) {
                e.currentTarget.style.backgroundColor = '#fff';
              }
            }}
          >
            {(() => {
              const titleText =
                target.title ||
                target.title_render?.replace(/^\d+(?:\.[0-9a-z]+)*\s*[·-]\s*/i, '') ||
                '';
              const trend = target?.computed?.target_trend_status;
              const { arrow, color } = trend ? getTrendArrow(trend) : { arrow: '—', color: '#bdbdbd' };
              const semaphoreTitle = target?.computed?.target_semaphore_status || 'Status not available';
              const displayIndex = target?.acf?.index || String(target?.id ?? '');

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {/* Row 1: ID, stretched space, then icon(s) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0', minWidth: 0 }}>
                    <span style={{ fontWeight: 700, color: '#334155', flexShrink: 0 }}>
                      {displayIndex}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }} aria-hidden="true" />
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      {/* Semaphore circle - hidden for UN */}
                      {!isUN && (
                        <div
                          style={{
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(target?.computed?.target_semaphore_status),
                            border: '1px solid #fff',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            flexShrink: 0,
                          }}
                          title={semaphoreTitle}
                        />
                      )}

                      {/* Trend arrow (progress icon) */}
                      <span
                        style={{
                          color,
                          fontSize: '1.125rem',
                          fontWeight: 900,
                          display: 'inline-block',
                          minWidth: '1.125rem',
                          textAlign: 'center',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          lineHeight: 1,
                        }}
                        title={trend || 'Progress not available'}
                      >
                        {arrow}
                      </span>
                    </div>
                  </div>

                  {/* Row 2: Target title */}
                  <div style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.35 }}>
                    {titleText}
                  </div>
                </div>
              );
            })()}
          </Link>
        ))}
      </ListGroup>
    </div>
  );
};

export default TargetsList;


